import { PREDEFINED_CONTEXT_LOADED, PROCESS_NEW_VISITOR, VISITOR_CREATED, VISITOR_ID_FROM_AB_TASTY_TAG, VISITOR_ID_GENERATED, VISITOR_PROFILE_LOADED } from './../enum/FlagshipConstant';
import { FSSdkStatus, SDK_INFO, VISITOR_ID_ERROR } from '../enum/index';
import { hexToValue, logDebugSprintf, logError, uuidV4 } from '../utils/utils';
import { DefaultStrategy } from './DefaultStrategy';
import { EventEmitter } from '../depsNode.native';
import { NotReadyStrategy } from './NotReadyStrategy';
import { PanicStrategy } from './PanicStrategy';
import { NoConsentStrategy } from './NoConsentStrategy';
import { MurmurHash } from '../utils/MurmurHash';
import { FSFetchStatus } from '../enum/FSFetchStatus';
import { FSFetchReasons } from '../enum/FSFetchReasons';
export class VisitorAbstract extends EventEmitter {
    _visitorId;
    _context;
    _flags;
    _configManager;
    _campaigns;
    _hasConsented;
    _anonymousId;
    deDuplicationCache;
    _isCleaningDeDuplicationCache;
    visitorCache;
    _exposedVariations;
    _sendExposedVariationTimeoutId;
    _instanceId;
    _traffic;
    _sdkInitialData;
    _consentHitTroubleshooting;
    _segmentHitTroubleshooting;
    _fetchStatus;
    _onFetchFlagsStatusChanged;
    _getCampaignsPromise;
    _hasContextBeenUpdated;
    _emotionAi;
    _analyticTraffic;
    _murmurHash;
    _visitorProfileCache;
    _isClientSuppliedID;
    _visitorVariationState;
    get visitorVariationState() {
        return this._visitorVariationState;
    }
    get isClientSuppliedID() {
        return this._isClientSuppliedID;
    }
    get hasContextBeenUpdated() {
        return this._hasContextBeenUpdated;
    }
    set hasContextBeenUpdated(v) {
        this._hasContextBeenUpdated = v;
    }
    get getCampaignsPromise() {
        return this._getCampaignsPromise;
    }
    set getCampaignsPromise(v) {
        this._getCampaignsPromise = v;
    }
    get onFetchFlagsStatusChanged() {
        return this._onFetchFlagsStatusChanged;
    }
    set onFetchFlagsStatusChanged(v) {
        this._onFetchFlagsStatusChanged = v;
    }
    get flagsStatus() {
        return this._fetchStatus;
    }
    set flagsStatus(v) {
        this._fetchStatus = v;
        if (this.onFetchFlagsStatusChanged) {
            this.onFetchFlagsStatusChanged(v);
        }
    }
    get segmentHitTroubleshooting() {
        return this._segmentHitTroubleshooting;
    }
    set segmentHitTroubleshooting(v) {
        this._segmentHitTroubleshooting = v;
    }
    get consentHitTroubleshooting() {
        return this._consentHitTroubleshooting;
    }
    set consentHitTroubleshooting(v) {
        this._consentHitTroubleshooting = v;
    }
    get sdkInitialData() {
        return this._sdkInitialData;
    }
    static SdkStatus;
    getSdkStatus() {
        return VisitorAbstract.SdkStatus;
    }
    lastFetchFlagsTimestamp = 0;
    _visitorCacheStatus;
    get visitorCacheStatus() {
        return this._visitorCacheStatus;
    }
    set visitorCacheStatus(v) {
        this._visitorCacheStatus = v;
    }
    get emotionAi() {
        return this._emotionAi;
    }
    get analyticTraffic() {
        return this._analyticTraffic;
    }
    initBaseProperties(param) {
        const { configManager, emotionAi, monitoringData, visitorProfileCache } = param;
        this._murmurHash = param.murmurHash || new MurmurHash();
        this._emotionAi = emotionAi;
        this._hasContextBeenUpdated = true;
        this._exposedVariations = {};
        this._sdkInitialData = monitoringData;
        this._instanceId = uuidV4();
        this._isCleaningDeDuplicationCache = false;
        this.deDuplicationCache = {};
        this._context = {};
        this._configManager = configManager;
        this.campaigns = [];
        this._visitorProfileCache = visitorProfileCache;
    }
    /**
     * Attempts to retrieve a visitor ID from the ABTasty tag in browser environments
     * @returns The ABTasty visitor ID if available, otherwise undefined
     */
    getVisitorIdFromTag() {
        if (__fsWebpackIsBrowser__) {
            const isClientSuppliedID = window.ABTasty?.api?.internal?._isByoidConfigured?.();
            if (isClientSuppliedID) {
                return undefined;
            }
            const visitorId = window.ABTasty?.api?.v1?.getValue('visitorId');
            if (visitorId) {
                logDebugSprintf(this.config, PROCESS_NEW_VISITOR, VISITOR_ID_FROM_AB_TASTY_TAG, visitorId);
            }
            return visitorId;
        }
        return undefined;
    }
    hasVisitorProfileClientSuppliedId(visitorProfileCache) {
        return visitorProfileCache.isClientSuppliedId === undefined ? true : visitorProfileCache.isClientSuppliedId;
    }
    initVisitorId(visitorId, isAuthenticated, hasConsented) {
        const shouldUseCache = this.config.reuseVisitorIds && hasConsented === true;
        const visitorCache = shouldUseCache ? this._visitorProfileCache?.loadVisitorProfile() : null;
        if (visitorCache) {
            logDebugSprintf(this.config, PROCESS_NEW_VISITOR, VISITOR_PROFILE_LOADED, visitorCache);
        }
        this._isClientSuppliedID = false;
        if (visitorId) {
            this.visitorId = visitorId;
            this._isClientSuppliedID = true;
        }
        else if (!isAuthenticated && visitorCache?.anonymousId) {
            this.visitorId = visitorCache.anonymousId;
            this._isClientSuppliedID = this.hasVisitorProfileClientSuppliedId(visitorCache);
        }
        else if (visitorCache?.visitorId) {
            this.visitorId = visitorCache.visitorId;
            this._isClientSuppliedID = this.hasVisitorProfileClientSuppliedId(visitorCache);
        }
        else {
            this.visitorId = this.getVisitorIdFromTag() || this.generateVisitorId();
        }
        this._anonymousId = null;
        if (isAuthenticated) {
            this._anonymousId = visitorCache?.anonymousId || uuidV4();
        }
    }
    constructor(param) {
        const { visitorId, context, isAuthenticated, hasConsented, initialFlagsData, initialCampaigns, onFlagsStatusChanged: onFetchFlagsStatusChanged } = param;
        super();
        this._visitorVariationState = param.visitorVariationState || {};
        this.initBaseProperties(param);
        this.initVisitorId(visitorId, isAuthenticated, hasConsented);
        this.initAnalyticTraffic();
        this.setConsent(hasConsented || false);
        this.updateContext(context);
        this.loadPredefinedContext();
        logDebugSprintf(this.config, PROCESS_NEW_VISITOR, PREDEFINED_CONTEXT_LOADED, {
            fs_client: SDK_INFO.name,
            fs_version: SDK_INFO.version,
            fs_users: this.visitorId
        });
        this.updateCache();
        const strategy = this.getStrategy();
        this.setInitialFlags(initialFlagsData, strategy);
        this.setInitializeCampaigns(initialCampaigns, !!initialFlagsData, strategy);
        this.onFetchFlagsStatusChanged = onFetchFlagsStatusChanged;
        this.updateFlagsStatus();
        this._emotionAi.init(this);
        logDebugSprintf(this.config, PROCESS_NEW_VISITOR, VISITOR_CREATED, this.visitorId, this.context, !!isAuthenticated, !!this.hasConsented);
    }
    updateFlagsStatus() {
        if (this._flags.size > 0) {
            this.flagsStatus = {
                status: FSFetchStatus.INITIAL_DATA,
                reason: FSFetchReasons.NONE
            };
            return;
        }
        this.flagsStatus = {
            status: FSFetchStatus.FETCH_REQUIRED,
            reason: FSFetchReasons.FLAGS_NEVER_FETCHED
        };
    }
    updateCache() {
        const visitorProfile = this.hasConsented ? {
            visitorId: this.visitorId,
            anonymousId: this.anonymousId,
            isClientSuppliedId: this.isClientSuppliedID
        } : undefined;
        this._visitorProfileCache?.saveVisitorProfile(visitorProfile);
    }
    get traffic() {
        return this._traffic;
    }
    set traffic(v) {
        this._traffic = v;
    }
    get instanceId() {
        return this._instanceId;
    }
    getCurrentDateTime() {
        return new Date();
    }
    initAnalyticTraffic() {
        const uniqueId = this.visitorId + this.getCurrentDateTime().toDateString();
        const hash = this._murmurHash.murmurHash3Int32(uniqueId);
        this._analyticTraffic = hash % 1000;
    }
    generateVisitorId() {
        const visitorId = uuidV4();
        logDebugSprintf(this.config, PROCESS_NEW_VISITOR, VISITOR_ID_GENERATED, visitorId);
        return visitorId;
    }
    clearDeDuplicationCache(deDuplicationTime) {
        if (this._isCleaningDeDuplicationCache) {
            return;
        }
        this._isCleaningDeDuplicationCache = true;
        const entries = Object.entries(this.deDuplicationCache);
        for (const [key, value] of entries) {
            if ((Date.now() - value) > (deDuplicationTime * 1000)) {
                delete this.deDuplicationCache[key];
            }
        }
        this._isCleaningDeDuplicationCache = false;
    }
    setInitialFlags(flags, strategy) {
        this._flags = new Map();
        if (!Array.isArray(flags)) {
            return;
        }
        flags.forEach((item) => {
            this._flags.set(item.key, {
                key: item.key,
                campaignId: item.campaignId,
                campaignName: item.campaignName,
                variationGroupId: item.variationGroupId,
                variationGroupName: item.variationGroupName,
                variationId: item.variationId,
                variationName: item.variationName,
                isReference: item.isReference,
                value: hexToValue(item.hex, this.config)?.v,
                slug: item.slug,
                campaignType: item.campaignType
            });
        });
        strategy?.sendVisitorAllocatedVariations();
    }
    setInitializeCampaigns(campaigns, hasInitialFlags, strategy) {
        if (campaigns && Array.isArray(campaigns) && !hasInitialFlags) {
            strategy?.updateCampaigns(campaigns);
            strategy?.sendVisitorAllocatedVariations();
        }
    }
    loadPredefinedContext() {
        this.context.fs_client = SDK_INFO.name;
        this.context.fs_version = SDK_INFO.version;
        this.context.fs_users = this.visitorId;
    }
    get visitorId() {
        return this._visitorId;
    }
    set visitorId(v) {
        if (!v || typeof v !== 'string') {
            logError(this.config, VISITOR_ID_ERROR, 'VISITOR ID');
            return;
        }
        this._visitorId = v;
        this.loadPredefinedContext();
        this.visitorCache = undefined;
    }
    get hasConsented() {
        return this._hasConsented;
    }
    set hasConsented(v) {
        this._hasConsented = v;
    }
    setConsent(hasConsented) {
        this.hasConsented = hasConsented;
        this.getStrategy().setConsent(hasConsented);
    }
    get context() {
        return this._context;
    }
    set context(v) {
        this._context = {};
        this.updateContext(v);
    }
    get flagsData() {
        return this._flags;
    }
    set flagsData(v) {
        this._flags = v;
    }
    get configManager() {
        return this._configManager;
    }
    get config() {
        return this.configManager.config;
    }
    get campaigns() {
        return this._campaigns;
    }
    set campaigns(v) {
        this._campaigns = v;
    }
    get anonymousId() {
        return this._anonymousId;
    }
    set anonymousId(v) {
        this._anonymousId = v;
    }
    getStrategy() {
        let strategy;
        const params = {
            visitor: this,
            murmurHash: new MurmurHash()
        };
        const status = this.getSdkStatus();
        if (status === undefined || status === FSSdkStatus.SDK_NOT_INITIALIZED) {
            strategy = new NotReadyStrategy(params);
        }
        else if (status === FSSdkStatus.SDK_PANIC) {
            strategy = new PanicStrategy(params);
        }
        else if (!this.hasConsented) {
            strategy = new NoConsentStrategy(params);
        }
        else {
            strategy = new DefaultStrategy(params);
        }
        return strategy;
    }
    async sendExposedVariation(flag) {
        if (__fsWebpackIsBrowser__ || __fsWebpackIsReactNative__) {
            if (!flag) {
                return;
            }
            this._exposedVariations[flag.campaignId] = {
                campaignId: flag.campaignId,
                variationGroupId: flag.variationGroupId,
                variationId: flag.variationId
            };
            this._visitorVariationState.exposedVariations = this._exposedVariations;
            if (!this.config.isQAModeEnabled) {
                return;
            }
            const BATCH_SIZE = 10;
            const DELAY = 100;
            const message = await import('../qaAssistant/common/handleMessage.ts');
            if (Object.keys(this._exposedVariations).length >= BATCH_SIZE) {
                message.sendVisitorExposedVariations(this._visitorVariationState);
                this._exposedVariations = {};
            }
            if (this._sendExposedVariationTimeoutId) {
                clearTimeout(this._sendExposedVariationTimeoutId);
            }
            if (Object.keys(this._exposedVariations).length === 0) {
                return;
            }
            this._sendExposedVariationTimeoutId = setTimeout(() => {
                message.sendVisitorExposedVariations(this._visitorVariationState);
                this._exposedVariations = {};
            }, DELAY);
        }
    }
    collectEAIEventsAsync(currentPage) {
        return this.getStrategy().collectEAIEventsAsync(currentPage);
    }
    sendEaiVisitorEvent(event) {
        this.getStrategy().reportEaiVisitorEvent(event);
    }
    sendEaiPageView(pageView) {
        this.getStrategy().reportEaiPageView(pageView);
    }
    onEAICollectStatusChange(callback) {
        this.getStrategy().onEAICollectStatusChange(callback);
    }
    cleanup() {
        this.getStrategy().cleanup();
    }
    async getCachedEAIScore() {
        if (!this.visitorCache) {
            await this.getStrategy().lookupVisitor();
        }
        return this.visitorCache?.data?.eAIScore;
    }
    async isEAIDataCollected() {
        if (!this.visitorCache) {
            await this.getStrategy().lookupVisitor();
        }
        return this.visitorCache?.data?.isEAIDataCollected || false;
    }
    async setCachedEAIScore(eAIScore) {
        this.getStrategy().cacheVisitor(eAIScore);
    }
    async setIsEAIDataCollected(isEAIDataCollected) {
        this.getStrategy().cacheVisitor(undefined, isEAIDataCollected);
    }
    sendTroubleshooting(hit) {
        return this.getStrategy().sendTroubleshootingHit(hit);
    }
    sendUsageHit(hit) {
        return this.getStrategy().sendUsageHit(hit);
    }
    addInTrackingManager(hit) {
        return this.getStrategy().addInTrackingManager(hit);
    }
}
